/**
 * SettingsScreen - Écran des paramètres
 * Accès au profil, préférences, et déconnexion
 */

import React, {useState} from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Image,
  Platform,
  SafeAreaView,
  Alert,
} from 'react-native';
import {useNavigation} from '@react-navigation/native';
import {useAuth} from '../contexts/AuthContext';
import {FontAwesomeIcon} from '@fortawesome/react-native-fontawesome';
import {faDoorOpen, faUserCircle, faArrowLeft, faTrashAlt} from '@fortawesome/free-solid-svg-icons';
import {LightTheme} from '../theme/theme';
import gameStateService from '../services/gameStateService';

const SettingsScreen: React.FC = () => {
  const navigation = useNavigation();
  const {user, signOut} = useAuth();
  const [isResetting, setIsResetting] = useState(false);

  const userName = (user?.user_metadata?.name as string) || user?.email || 'Utilisateur';
  const userEmail = user?.email || '';
  const userPicture = (user?.user_metadata?.picture as string) || '';

  const handleSignOut = () => {
    signOut();
  };

  const handleResetAllGames = () => {
    // Confirmation double selon les bonnes pratiques UX (action destructive)
    Alert.alert(
      'Réinitialiser toutes les parties ?',
      'Toutes vos parties en cours seront supprimées. Cette action est irréversible.',
      [
        {
          text: 'Annuler',
          style: 'cancel',
        },
        {
          text: 'Confirmer',
          style: 'destructive',
          onPress: () => {
            // Deuxième confirmation pour action critique
            Alert.alert(
              'Êtes-vous absolument sûr ?',
              'Toutes vos sauvegardes seront définitivement supprimées. Vous ne pourrez pas annuler cette action.',
              [
                {
                  text: 'Annuler',
                  style: 'cancel',
                },
                {
                  text: 'Supprimer toutes les parties',
                  style: 'destructive',
                  onPress: async () => {
                    try {
                      setIsResetting(true);
                      const allGameStates = await gameStateService.getAllGameStates();
                      
                      // Supprimer toutes les sauvegardes
                      const deletePromises = allGameStates.map((gameState) =>
                        gameStateService.deleteGameState(gameState.uniqueURI, gameState.slot),
                      );
                      
                      await Promise.all(deletePromises);
                      
                      Alert.alert(
                        'Succès',
                        `${allGameStates.length} partie(s) supprimée(s) avec succès.`,
                        [{text: 'OK'}],
                      );
                    } catch (error: any) {
                      console.error('[SettingsScreen] Error resetting all games:', error);
                      Alert.alert(
                        'Erreur',
                        'Impossible de supprimer toutes les parties. Veuillez réessayer.',
                      );
                    } finally {
                      setIsResetting(false);
                    }
                  },
                },
              ],
            );
          },
        },
      ],
    );
  };

  return (
    <SafeAreaView style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}>
          <FontAwesomeIcon icon={faArrowLeft} size={20} color="#E8E6E1" />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Paramètres</Text>
        <View style={styles.headerSpacer} />
      </View>

      <ScrollView style={styles.scrollView} contentContainerStyle={styles.content}>
        {/* Profil */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Profil</Text>
          <View style={styles.profileCard}>
            <View style={styles.avatarContainer}>
              {userPicture ? (
                <Image source={{uri: userPicture}} style={styles.avatar} />
              ) : (
                <View style={styles.defaultAvatar}>
                  <FontAwesomeIcon icon={faUserCircle} size={48} color="#8B4513" />
                </View>
              )}
            </View>
            <View style={styles.profileInfo}>
              <Text style={styles.profileName}>{userName}</Text>
              <Text style={styles.profileEmail}>{userEmail}</Text>
            </View>
          </View>
        </View>

        {/* Préférences (à venir) */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Préférences</Text>
          <View style={styles.preferencesPlaceholder}>
            <Text style={styles.placeholderText}>Les préférences seront disponibles prochainement</Text>
          </View>
        </View>

        {/* Espace flexible */}
        <View style={styles.spacer} />

        {/* Déconnexion */}
        <View style={styles.section}>
          <TouchableOpacity
            style={styles.signOutButton}
            onPress={handleSignOut}
            activeOpacity={0.85}>
            <FontAwesomeIcon icon={faDoorOpen} size={18} color="#6B7280" />
            <Text style={styles.signOutText}>Se déconnecter</Text>
          </TouchableOpacity>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: LightTheme.colors.bg,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: '#2C2C2C',
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0, 0, 0, 0.1)',
  },
  backButton: {
    padding: 8,
    marginRight: 8,
  },
  headerTitle: {
    flex: 1,
    fontSize: 18,
    fontWeight: '600',
    color: '#E8E6E1',
    textAlign: 'center',
    fontFamily: Platform.OS === 'web' ? 'Cinzel, serif' : undefined,
  },
  headerSpacer: {
    width: 36, // Équivalent à la taille du backButton pour centrer le titre
  },
  scrollView: {
    flex: 1,
  },
  content: {
    padding: 20,
    paddingBottom: 32,
  },
  section: {
    marginBottom: 32,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#2C3E50',
    marginBottom: 16,
    fontFamily: Platform.OS === 'web' ? 'Cinzel, serif' : undefined,
    letterSpacing: 0.5,
  },
  profileCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F5F3ED',
    ...Platform.select({
      web: {
        borderTopLeftRadius: 12,
        borderTopRightRadius: 14,
        borderBottomRightRadius: 14,
        borderBottomLeftRadius: 10,
      },
      default: {
        borderRadius: 14,
      },
    }),
    padding: 20,
    borderWidth: 1,
    borderColor: '#D4C4B0',
    ...Platform.select({
      web: {
        boxShadow: '0 2px 6px rgba(0, 0, 0, 0.08)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: {width: 0, height: 1},
        shadowOpacity: 0.06,
        shadowRadius: 4,
        elevation: 2,
      },
    }),
  },
  avatarContainer: {
    marginRight: 16,
  },
  avatar: {
    width: 64,
    height: 64,
    borderRadius: 32,
    borderWidth: 2,
    borderColor: '#C4B5A0',
  },
  defaultAvatar: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: '#E8D5C4',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 2,
    borderColor: '#C4B5A0',
  },
  profileInfo: {
    flex: 1,
  },
  profileName: {
    fontSize: 18,
    fontWeight: '600',
    color: '#2C3E50',
    marginBottom: 4,
  },
  profileEmail: {
    fontSize: 14,
    color: '#6B7280',
  },
  preferencesPlaceholder: {
    backgroundColor: '#F5F3ED',
    ...Platform.select({
      web: {
        borderTopLeftRadius: 10,
        borderTopRightRadius: 12,
        borderBottomRightRadius: 12,
        borderBottomLeftRadius: 8,
      },
      default: {
        borderRadius: 12,
      },
    }),
    padding: 20,
    borderWidth: 1,
    borderColor: '#D4C4B0',
  },
  placeholderText: {
    fontSize: 14,
    color: '#8A8A8A',
    fontStyle: 'italic',
    textAlign: 'center',
  },
  resetButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F5F3ED',
    ...Platform.select({
      web: {
        borderTopLeftRadius: 10,
        borderTopRightRadius: 12,
        borderBottomRightRadius: 12,
        borderBottomLeftRadius: 8,
      },
      default: {
        borderRadius: 12,
      },
    }),
    padding: 16,
    borderWidth: 1,
    borderColor: '#D4C4B0',
    marginTop: 8,
    opacity: 1,
  },
  resetButtonContent: {
    flex: 1,
    marginLeft: 12,
  },
  resetButtonText: {
    fontSize: 16,
    color: '#D32F2F',
    fontWeight: '500',
    marginBottom: 4,
  },
  resetButtonSubtext: {
    fontSize: 12,
    color: '#8A8A8A',
    fontStyle: 'italic',
  },
  spacer: {
    flex: 1,
    minHeight: 40,
  },
  signOutButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#F5F3ED',
    ...Platform.select({
      web: {
        borderTopLeftRadius: 10,
        borderTopRightRadius: 12,
        borderBottomRightRadius: 12,
        borderBottomLeftRadius: 8,
      },
      default: {
        borderRadius: 12,
      },
    }),
    padding: 16,
    borderWidth: 1,
    borderColor: '#D4C4B0',
    marginTop: 8,
  },
  signOutText: {
    fontSize: 16,
    color: '#6B7280',
    fontWeight: '400',
    marginLeft: 12,
  },
});

export default SettingsScreen;
